import * as THREE from 'three';

/**
 * Width of an octahedron as a fraction of its bone's length (Blender-style),
 * clamped so tiny bones (finger joints) stay visible and long bones don't
 * balloon. This is what keeps proportions right without a thickness slider.
 */
const WIDTH_RATIO = 0.1;
const MIN_WIDTH = 0.004; // metres
const MAX_WIDTH = 0.05;

/** Bulge position along the bone, as a fraction of its length. */
const BULGE_AT = 0.1;

/** One octahedron spanning head joint → tail joint. */
interface Segment {
  mesh: THREE.Mesh;
  head: THREE.Object3D;
  tail: THREE.Object3D;
}

/**
 * Unit octahedron along +Y: tips at y=0 and y=1, diamond ring (±X/±Z) at
 * y=BULGE_AT with radius 1. Per segment it's scaled (width, length, width),
 * which lands the ring at 10% of the bone length — the classic rigging look.
 */
function buildUnitOctahedron(): THREE.BufferGeometry {
  // prettier-ignore
  const v = [
    [0, 0, 0],           // 0 head tip
    [0, 1, 0],           // 1 tail tip
    [1, BULGE_AT, 0],    // 2 +x
    [0, BULGE_AT, 1],    // 3 +z
    [-1, BULGE_AT, 0],   // 4 -x
    [0, BULGE_AT, -1],   // 5 -z
  ];
  // prettier-ignore
  const faces = [
    0, 2, 3,  0, 3, 4,  0, 4, 5,  0, 5, 2, // head cap
    1, 3, 2,  1, 4, 3,  1, 5, 4,  1, 2, 5, // tail cap
  ];
  const positions = new Float32Array(faces.length * 3);
  for (let i = 0; i < faces.length; i++) positions.set(v[faces[i]], i * 3);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.computeVertexNormals(); // non-indexed → per-face (faceted) normals
  return geometry;
}

/**
 * Blender-style octahedral bone visualization for one actor skeleton: one
 * octahedron per parent→child bone pair (leaf bones have no length in FBX, so
 * like SkeletonHelper they draw nothing of their own).
 *
 * Follows the SkeletonHelper pattern: lives at the scene root and re-derives
 * every segment's world matrix from the bones' matrixWorld inside
 * updateMatrixWorld, so it's fresh whenever the renderer draws — no per-tick
 * wiring, and no one-frame lag after a scrub.
 */
export class OctahedronBoneHelper extends THREE.Group {
  readonly material: THREE.MeshStandardMaterial;
  private readonly geometry: THREE.BufferGeometry;
  private readonly segments: Segment[] = [];

  constructor(bones: THREE.Object3D[]) {
    super();
    this.geometry = buildUnitOctahedron();
    this.material = new THREE.MeshStandardMaterial({
      color: 0xcccccc,
      flatShading: true,
      roughness: 0.55,
      metalness: 0.15,
    });

    const set = new Set(bones);
    for (const bone of bones) {
      for (const child of bone.children) {
        if (!set.has(child)) continue;
        const mesh = new THREE.Mesh(this.geometry, this.material);
        mesh.castShadow = true; // ground-shadow feature; free while shadows are off
        mesh.matrixAutoUpdate = false;
        this.add(mesh);
        this.segments.push({ mesh, head: bone, tail: child });
      }
    }
  }

  override updateMatrixWorld(force?: boolean): void {
    super.updateMatrixWorld(force);
    for (const seg of this.segments) {
      _head.setFromMatrixPosition(seg.head.matrixWorld);
      _tail.setFromMatrixPosition(seg.tail.matrixWorld);
      _dir.subVectors(_tail, _head);
      const len = _dir.length();
      // Zero-length segments (co-located duplicate joints) have no direction.
      seg.mesh.visible = len > 1e-5;
      if (!seg.mesh.visible) continue;
      _quat.setFromUnitVectors(_up, _dir.divideScalar(len));
      const width = Math.min(Math.max(len * WIDTH_RATIO, MIN_WIDTH), MAX_WIDTH);
      _scale.set(width, len, width);
      seg.mesh.matrixWorld.compose(_head, _quat, _scale);
    }
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

const _head = new THREE.Vector3();
const _tail = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _scale = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
